// src/pages/DocumentosFiscalesVista.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Snackbar, Alert,
  Paper, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Autocomplete, TextField, IconButton, FormControlLabel, Checkbox,
  Pagination,
} from '@mui/material';
import {
  Link as LinkIcon, LinkOff as LinkOffIcon, Description as DescriptionIcon,
  Delete as DeleteIcon, Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../api/api';

// Función para decodificar el payload del JWT (igual que en PantallaCola)
const decodeToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error decodificando token:', e);
    return null;
  }
};

const DocumentosFiscalesVista = () => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // Agregado para el total de documentos
  const [searchQuery, setSearchQuery] = useState('');

  // Clientes para vinculación
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [camposBusqueda, setCamposBusqueda] = useState(['DUI', 'NIT']);

  // Diálogos
  const [openDialog, setOpenDialog] = useState(false);
  const [docSeleccionado, setDocSeleccionado] = useState(null);
  const [clienteBuscado, setClienteBuscado] = useState(null);

  const [openNotaDialog, setOpenNotaDialog] = useState(false);
  const [docNota, setDocNota] = useState(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [docAEliminar, setDocAEliminar] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const rowsPerPage = 20;

  // Decodificar token una sola vez
  const token = localStorage.getItem('token');
  const payload = useMemo(() => decodeToken(token), [token]);

  // Sucursal actual del token (la que el usuario tiene seleccionada)
  const sucursalId = payload?.id_sucursal;

  // === DEPURACIÓN DEL PAYLOAD DEL TOKEN (NUEVO) ===
  useEffect(() => {
    console.group('🔍 DEPURACIÓN Documentos Fiscales');
    console.log('Payload del token:', payload);
    console.log('Sucursal actual (id_sucursal):', payload?.id_sucursal);
    console.log('Nombre sucursal:', payload?.nombre_sucursal);
    console.log('Veterinaria:', payload?.nombre_veterinaria);
    console.groupEnd();
  }, [payload]);
  // ===============================================

  // Cargar clientes (no cambia)
  const buscarClientes = async (query = '') => {
    setLoadingClientes(true);
    try {
      const res = await api.get('/clientes/listado', { params: { q: query } });
      setClientes(res.data.map(c => ({
        id_cliente: c.id_cliente,
        nombre_cliente: c.nombre_cliente || 'Sin nombre',
        dui: c.dui || '',
        nit: c.nit || '',
        nrc: c.nrc || '',
        pasaporte: c.pasaporte || '',
        carnet_de_residencia: c.carnet_de_residencia || ''
      })));
    } catch (err) {
      console.error("Error cargando clientes", err);
    } finally {
      setLoadingClientes(false);
    }
  };

  useEffect(() => {
    buscarClientes('');
  }, []);

  // === FUNCIÓN DE CARGA CON DEPURACIÓN (MODIFICADA) ===
  // Convertido a useCallback para asegurar que las dependencias reactivas sean correctas
  const fetchDocumentos = useCallback(async (newPage = 1) => {
    const params = {
      page: newPage,
      limit: rowsPerPage,
      q: searchQuery || undefined,
      id_sucursal: sucursalId || undefined
    };

    console.group('📡 LLAMADA AL BACKEND /documentos_fiscales/listado');
    console.log('Parámetros enviados:', params);
    // Construir URL completa para depuración
    const urlParams = Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined));
    console.log('URL completa:', '/documentos_fiscales/listado?' + new URLSearchParams(urlParams).toString());

    setLoading(true);
    try {
      const res = await api.get('/documentos_fiscales/listado', { params });
      
      console.log('Respuesta completa del backend:', res.data);
      console.log(`Documentos recibidos: ${res.data.data?.length || 0}`);
      console.log('Paginación:', {
        page: res.data.page,
        total_pages: res.data.pages,
        total_documentos: res.data.total
      });

      setDocumentos(res.data.data || []);
      setTotalPages(res.data.pages || 1);
      setTotalItems(res.data.total || 0); // Establecer el total
      setPage(res.data.page || 1);
    } catch (err) {
      console.error('Error en fetchDocumentos:', err);
      console.error('Detalle del error:', err.response?.data || err.message);
      setSnackbarMsg('Error al cargar documentos');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  }, [searchQuery, sucursalId, rowsPerPage]); // Dependencias necesarias
  // ======================================================

  // Recargar cuando cambie búsqueda o sucursal
  useEffect(() => {
    fetchDocumentos(1);
  }, [searchQuery, sucursalId, fetchDocumentos]);

  const handlePageChange = (event, value) => {
    fetchDocumentos(value);
  };

  // Acciones (modificadas para usar fetchDocumentos)
  const handleVincular = (doc) => {
    setDocSeleccionado(doc);
    setClienteBuscado(null);
    setOpenDialog(true);
  };

  const handleDesvincular = async (docId) => {
    if (!window.confirm("¿Desvincular este documento?")) return;
    try {
      await api.put(`/documentos_fiscales/${docId}/desvincular`);
      setSnackbarMsg("Documento desvinculado");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      fetchDocumentos(page); // Recarga la página actual
    } catch (err) {
      setSnackbarMsg("Error al desvincular");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleEliminar = (doc) => {
    setDocAEliminar(doc);
    setConfirmChecked(false);
    setOpenDeleteDialog(true);
  };

  const confirmarEliminacion = async () => {
    if (!confirmChecked) return;
    try {
      await api.delete(`/documentos_fiscales/${docAEliminar.id}`);
      setSnackbarMsg(`Documento ${docAEliminar.numero_control} eliminado correctamente`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setOpenDeleteDialog(false);
      fetchDocumentos(page); // Recarga la página actual
    } catch (err) {
      const mensajeError = err.response?.data?.detail || "Error al eliminar el documento";
      setSnackbarMsg(mensajeError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const confirmarVinculacion = async () => {
    if (!clienteBuscado) return;
    try {
      await api.put(`/documentos_fiscales/${docSeleccionado.id}/vincular`, {
        id_cliente: clienteBuscado.id_cliente
      });
      setSnackbarMsg(`Vinculado a ${clienteBuscado?.nombre_cliente || 'Cliente'}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenDialog(false);
      fetchDocumentos(page); // Recarga la página actual
    } catch (err) {
      setSnackbarMsg('Error al vincular');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(val || 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Documentos Fiscales
        </Typography>
        <Chip
          label={payload?.nombre_sucursal || 'Sucursal no disponible'}
          color="primary"
          variant="outlined"
          size="medium"
          sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
        />
      </Box>

      {/* BUSCADOR */}
      <Box sx={{ mb: 3, maxWidth: 500 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Buscar documento..."
          placeholder="Número de control, receptor, código..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            endAdornment: searchQuery && (
              <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                <ClearIcon />
              </IconButton>
            ),
          }}
        />
      </Box>

      {/* TABLA */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Fecha</strong></TableCell>
                <TableCell><strong>Número Control</strong></TableCell>
                <TableCell><strong>Receptor</strong></TableCell>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell><strong>Acción</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentos.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>{new Date(doc.fecha_emision).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title={doc.codigo_generacion || ''}>
                      <span>{doc.numero_control}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{doc.nombre_receptor || '—'}</TableCell>
                  <TableCell>{formatCurrency(doc.monto_total_pagar)}</TableCell>
                  <TableCell>
                    <Chip
                      label={doc.estado_vinculacion || 'N/A'}
                      size="small"
                      color={
                        doc.estado_vinculacion === 'PENDIENTE_MANUAL' ? 'warning' :
                        doc.estado_vinculacion?.includes('VINCULADO') ? 'success' : 'default'
                      }
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    {doc.estado_vinculacion === 'PENDIENTE_MANUAL' && (
                      <IconButton size="small" color="primary" onClick={() => handleVincular(doc)} title="Vincular">
                        <LinkIcon />
                      </IconButton>
                    )}
                    {doc.estado_vinculacion?.includes('VINCULADO') && (
                      <IconButton size="small" color="error" onClick={() => handleDesvincular(doc.id)} title="Desvincular">
                        <LinkOffIcon />
                      </IconButton>
                    )}
                    {doc.nota_aclaracion && (
                      <IconButton size="small" color="info" onClick={() => { setDocNota(doc); setOpenNotaDialog(true); }} title="Ver nota">
                        <DescriptionIcon />
                      </IconButton>
                    )}
                    {['PENDIENTE_MANUAL', 'VINCULADO_MANUAL', 'VINCULADO_AUTOMATICO'].includes(doc.estado_vinculacion) && (
                      <IconButton size="small" color="error" onClick={() => handleEliminar(doc)} title="Eliminar">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {documentos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="textSecondary">
                      {searchQuery
                        ? 'No se encontraron documentos con la búsqueda'
                        : 'No hay documentos disponibles'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* PAGINACIÓN */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Info de resultados */}
      {!loading && totalItems > 0 && (
        <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 3 }}>
          Mostrando {((page - 1) * rowsPerPage) + 1}–{Math.min(page * rowsPerPage, totalItems)}
          de {totalItems} documentos
          {searchQuery && ' (filtrados)'}
        </Typography>
      )}

      {/* DIÁLOGOS (sin cambios en la lógica) */}
      {/* Modal nota aclaración */}
      <Dialog open={openNotaDialog} onClose={() => setOpenNotaDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nota de aclaración</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {docNota?.nota_aclaracion || 'Sin nota'}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 2 }}>
            Documento: {docNota?.numero_control}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNotaDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal vincular */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vincular documento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {docSeleccionado?.numero_control}
          </Typography>
          <Typography variant="caption" display="block" gutterBottom>
            Receptor: {docSeleccionado?.nombre_receptor || 'Sin nombre'}
          </Typography>

          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Buscar por:</Typography>
            {['DUI', 'NIT', 'NRC', 'Pasaporte', 'Carnet Residencia'].map((campo) => (
              <FormControlLabel
                key={campo}
                control={
                  <Checkbox
                    size="small"
                    checked={camposBusqueda.includes(campo)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCamposBusqueda([...camposBusqueda, campo]);
                      } else {
                        setCamposBusqueda(camposBusqueda.filter(c => c !== campo));
                      }
                    }}
                  />
                }
                label={campo}
              />
            ))}
          </Box>

          <Autocomplete
            freeSolo
            options={clientes}
            loading={loadingClientes}
            getOptionKey={(option) => option.id_cliente}
            getOptionLabel={(option) => {
              const docs = [
                option.dui && `DUI: ${option.dui}`,
                option.nit && `NIT: ${option.nit}`,
                option.nrc && `NRC: ${option.nrc}`,
                option.pasaporte && `Pasaporte: ${option.pasaporte}`,
                option.carnet_de_residencia && `Carnet: ${option.carnet_de_residencia}`
              ].filter(Boolean);
              return `${option.nombre_cliente} ${docs.length > 0 ? '| ' + docs.join(' | ') : ''}`.trim();
            }}
            isOptionEqualToValue={(option, value) => option.id_cliente === value.id_cliente}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input' && newInputValue.length >= 3 && camposBusqueda.length > 0) {
                buscarClientes(newInputValue);
              }
            }}
            onChange={(e, value) => setClienteBuscado(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Escribe aquí el documento..."
                variant="outlined"
                fullWidth
                margin="normal"
                disabled={camposBusqueda.length === 0}
                helperText={camposBusqueda.length === 0 ? "Selecciona al menos un tipo de documento" : ""}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClientes ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={confirmarVinculacion} variant="contained" color="primary" disabled={!clienteBuscado}>
            Vincular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal eliminar */}
      {docAEliminar && (
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon /> Eliminar documento permanentemente
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Estás a punto de eliminar el siguiente documento fiscal:
            </Typography>
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, my: 2 }}>
              <Typography><strong>Número de Control:</strong> {docAEliminar.numero_control || '—'}</Typography>
              <Typography><strong>Receptor:</strong> {docAEliminar.nombre_receptor || '—'}</Typography>
              <Typography><strong>Total:</strong> {formatCurrency(docAEliminar.monto_total_pagar)}</Typography>
            </Box>
            <Alert severity="error" sx={{ mt: 2 }}>
              Esta acción <strong>NO se puede deshacer</strong>. El documento se eliminará para siempre.
            </Alert>
            <FormControlLabel
              control={<Checkbox checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} color="error" />}
              label="Entiendo que esta acción es irreversible y deseo eliminar el documento"
              sx={{ mt: 3 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
            <Button
              onClick={confirmarEliminacion}
              variant="contained"
              color="error"
              disabled={!confirmChecked}
              startIcon={<DeleteIcon />}
            >
              Eliminar permanentemente
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" elevation={6}
          sx={{ minWidth: 300, fontSize: '1rem', fontWeight: 500, borderRadius: 2, '& .MuiAlert-icon': { fontSize: 28 } }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentosFiscalesVista;